import { useEffect, useRef, useState } from "react";
import { NxTheme, NxIcon } from "../nx";

export const MAX_QUALIFICATION_DOCS = 5;
export const MAX_DOC_SIZE_MB = 5;
export const ACCEPTED_DOC_TYPES = ".pdf,.jpg,.jpeg,.png";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

export interface DocItem {
  key: string;
  name: string;
  size?: number;
  url: string;
  file?: File;
}

function extensionOf(value?: string) {
  return String(value || "").split("?")[0].split(".").pop()?.toLowerCase() || "";
}

/** Prefer the original filename for type detection, falling back to the URL. */
export function docKind(doc: { name?: string; url?: string }): "image" | "pdf" | "unknown" {
  const candidates = [doc.name, doc.url];
  for (const candidate of candidates) {
    const ext = extensionOf(candidate);
    if (IMAGE_EXTENSIONS.indexOf(ext) !== -1) return "image";
    if (ext === "pdf") return "pdf";
  }
  return "unknown";
}

export function formatSize(bytes?: number) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Build a DocItem for a freshly picked file, with a blob URL for previewing. */
export function docItemFromFile(file: File): DocItem {
  return {
    key: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    url: URL.createObjectURL(file),
    file,
  };
}

export function docItemsFromProfile(docs: any[], legacyUrl?: string): DocItem[] {
  if (Array.isArray(docs) && docs.length) {
    return docs
      .filter((d: any) => d && d.url)
      .map((d: any, i: number) => ({
        key: `${d.url}-${i}`,
        name: d.name || String(d.url).split("/").pop() || "Document",
        size: d.size,
        url: d.url,
      }));
  }
  if (legacyUrl) {
    return [{ key: legacyUrl, name: String(legacyUrl).split("/").pop() || "Document", url: legacyUrl }];
  }
  return [];
}

export function DocumentPreviewModal({
  t,
  doc,
  onClose,
}: {
  t: NxTheme;
  doc: DocItem | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doc, onClose]);

  if (!doc) return null;
  const kind = docKind(doc);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: t.paper, borderRadius: 12, border: `1px solid ${t.border}`,
          width: "100%", maxWidth: 900, height: "88vh", display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, padding: "14px 18px", borderBottom: `1px solid ${t.border}`,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: t.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {doc.name}
            </div>
            {doc.size ? <div style={{ fontSize: 12, color: t.inkMute }}>{formatSize(doc.size)}</div> : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <a
              href={doc.url}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 12, fontWeight: 500, color: t.ink, textDecoration: "none",
                border: `1px solid ${t.borderStrong}`, borderRadius: 6, padding: "6px 12px",
              }}
            >
              Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              style={{
                border: `1px solid ${t.borderStrong}`, background: "transparent", color: t.ink,
                borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>

        <div style={{ flex: 1, background: t.paperAlt, display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto" }}>
          {kind === "image" ? (
            <img src={doc.url} alt={doc.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          ) : kind === "pdf" ? (
            <iframe src={doc.url} title={doc.name} style={{ border: "none", width: "100%", height: "100%" }} />
          ) : (
            <div style={{ textAlign: "center", color: t.inkSoft, fontSize: 13, padding: 32 }}>
              This file type cannot be previewed here. Use “Open in new tab”.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DocRow({
  t,
  doc,
  onView,
  onRemove,
}: {
  t: NxTheme;
  doc: DocItem;
  onView: (doc: DocItem) => void;
  onRemove?: (doc: DocItem) => void;
}) {
  const kind = docKind(doc);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
      background: t.paperAlt, borderRadius: 8, border: `1px solid ${t.border}`,
    }}>
      {kind === "image" ? (
        <img
          src={doc.url}
          alt=""
          style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 4, border: `1px solid ${t.border}`, flexShrink: 0 }}
        />
      ) : (
        <span style={{ width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {NxIcon.doc(t.inkSoft)}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: t.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {doc.name}
        </div>
        {doc.size ? <div style={{ fontSize: 11, color: t.inkMute }}>{formatSize(doc.size)}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onView(doc)}
        style={{ border: "none", background: "none", color: t.accent, cursor: "pointer", fontSize: 12, fontWeight: 500, padding: 0 }}
      >
        View
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(doc)}
          style={{ border: "none", background: "none", color: t.danger, cursor: "pointer", fontSize: 12, fontWeight: 500, padding: 0 }}
        >
          Remove
        </button>
      )}
    </div>
  );
}

export function QualificationDocsPicker({
  t,
  docs,
  onChange,
  max = MAX_QUALIFICATION_DOCS,
  hasError,
  disabled,
  inputId = "qualification-docs-upload",
}: {
  t: NxTheme;
  docs: DocItem[];
  onChange: (docs: DocItem[]) => void;
  max?: number;
  hasError?: boolean;
  disabled?: boolean;
  inputId?: string;
}) {
  const [previewDoc, setPreviewDoc] = useState<DocItem | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string>("");

  const docsRef = useRef(docs);
  docsRef.current = docs;
  useEffect(() => () => {
    docsRef.current.forEach((doc) => { if (doc.file) URL.revokeObjectURL(doc.url); });
  }, []);

  const atLimit = docs.length >= max;

  const handleSelect = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const messages: string[] = [];
    const next = [...docs];

    Array.from(fileList).forEach((file) => {
      if (next.length >= max) {
        messages.push(`Only ${max} documents can be attached — “${file.name}” was skipped.`);
        return;
      }
      if (file.size > MAX_DOC_SIZE_MB * 1024 * 1024) {
        messages.push(`“${file.name}” is larger than ${MAX_DOC_SIZE_MB} MB.`);
        return;
      }
      const item = docItemFromFile(file);
      if (next.some((d) => d.key === item.key)) {
        URL.revokeObjectURL(item.url);
        messages.push(`“${file.name}” is already attached.`);
        return;
      }
      next.push(item);
    });

    setNotice(messages.join(" "));
    onChange(next);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemove = (doc: DocItem) => {
    if (doc.file) URL.revokeObjectURL(doc.url);
    setNotice("");
    if (previewDoc?.key === doc.key) setPreviewDoc(null);
    onChange(docs.filter((d) => d.key !== doc.key));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_DOC_TYPES}
        disabled={disabled || atLimit}
        onChange={(e) => handleSelect(e.target.files)}
        style={{ display: "none" }}
        id={inputId}
      />
      <label
        htmlFor={atLimit || disabled ? undefined : inputId}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          width: "100%", padding: "14px 0", background: t.surface,
          border: `1.5px dashed ${hasError ? t.danger : t.border}`, borderRadius: 10,
          color: t.inkSoft, fontSize: 13, textAlign: "center",
          cursor: atLimit || disabled ? "not-allowed" : "pointer",
          opacity: atLimit || disabled ? 0.6 : 1,
        }}
      >
        {NxIcon.upload(t.inkSoft)}
        {atLimit
          ? `Maximum ${max} documents attached`
          : docs.length > 0
            ? `Add more documents (${docs.length}/${max})`
            : `Select documents (up to ${max})`}
      </label>

      {docs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {docs.map((doc) => (
            <DocRow key={doc.key} t={t} doc={doc} onView={setPreviewDoc} onRemove={handleRemove} />
          ))}
        </div>
      )}

      {notice && <div style={{ fontSize: 12, color: t.warning }}>{notice}</div>}

      <DocumentPreviewModal t={t} doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
}
